import { useMemo } from "react";
import { Link, useParams } from "react-router-dom";

import { VideoCard } from "../components/VideoCard";
import { useCatalog } from "../data/hooks";
import { speakerByKey } from "../lib/speakers";
import type { CatalogEntry } from "../lib/types";

interface SpeakerGroup {
  readonly key: string;
  readonly name: string;
  readonly count: number;
}

function groupSpeakers(entries: readonly CatalogEntry[]): SpeakerGroup[] {
  const names = new Map<string, string>();
  const counts = new Map<string, number>();
  for (const entry of entries) {
    names.set(entry.speakerKey, entry.speaker);
    counts.set(entry.speakerKey, (counts.get(entry.speakerKey) ?? 0) + 1);
  }
  return [...names.entries()]
    .map(([key, name]) => ({ key, name, count: counts.get(key) ?? 0 }))
    .sort((a, b) => b.count - a.count);
}

export function SpeakersIndex() {
  const { entries } = useCatalog();
  const groups = useMemo(
    () => (entries === null ? [] : groupSpeakers(entries)),
    [entries],
  );

  return (
    <div>
      <header className="mb-8">
        <p className="eyebrow">Speakers</p>
        <h1 className="font-display text-3xl text-warm-brown">
          Who is speaking
        </h1>
      </header>
      {entries === null ? (
        <p className="text-warm-gray">Loading…</p>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2">
          {groups.map((group) => (
            <Link
              key={group.key}
              to={`/speakers/${group.key}`}
              className="plate flex items-center justify-between gap-4 p-4 transition-transform hover:-translate-y-0.5"
            >
              <span className="font-serif text-lg text-warm-brown">
                {group.name}
              </span>
              <span className="shrink-0 text-sm text-terra">{group.count}</span>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

export function SpeakerDetail() {
  const params = useParams();
  const key = params.key ?? "";
  const { entries } = useCatalog();
  const speaker = speakerByKey(key);

  const videos = useMemo(
    () =>
      (entries ?? [])
        .filter((entry) => entry.speakerKey === key)
        .sort((a, b) => (b.year ?? 0) - (a.year ?? 0)),
    [entries, key],
  );

  const gems = useMemo(() => {
    const found: string[] = [];
    for (const entry of videos) {
      if (entry.gem !== null && !found.includes(entry.gem)) {
        found.push(entry.gem);
      }
      if (found.length >= 6) {
        break;
      }
    }
    return found;
  }, [videos]);

  return (
    <div>
      <header className="mb-8">
        <Link
          to="/speakers"
          className="text-xs text-warm-gray hover:text-terra"
        >
          Speakers
        </Link>
        <h1 className="mt-1 font-display text-3xl text-warm-brown">
          {speaker.name}
        </h1>
        <p className="mt-1 text-warm-gray">{videos.length} discourses</p>
      </header>

      {gems.length > 0 ? (
        <div className="mb-10">
          <p className="eyebrow mb-3">Highlights</p>
          <ul className="grid gap-3 sm:grid-cols-2">
            {gems.map((gem) => (
              <li
                key={gem}
                className="border-l-2 border-gold/40 pl-3 font-serif italic text-warm-brown-light"
              >
                {gem}
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {videos.length > 0 ? (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {videos.map((entry) => (
            <VideoCard key={entry.id} entry={entry} />
          ))}
        </div>
      ) : (
        <p className="text-warm-gray">No discourses for this speaker.</p>
      )}
    </div>
  );
}
